// src/reducers/sumoSimulationSlice.js

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import JSZip from "jszip";

const API_BASE_URL =
  process.env.REACT_APP_SUMO_API_BASE_URL ||
  "https://data-platform.cds-probono.eu/sumo";

const AUTH_TOKEN = process.env.REACT_APP_SUMO_AUTH_TOKEN;

const getHeaders = (hasBody = false) => {
  const headers = {
    accept: "application/json",
    Authorization: `Basic ${AUTH_TOKEN}`,
  };

  if (hasBody) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
};

const getErrorMessage = async (response, fallbackMessage) => {
  try {
    const text = await response.text();
    return text || fallbackMessage;
  } catch {
    return fallbackMessage;
  }
};

/* ------------------------------------------------------------------ */
/* SUMO output parsing (client-side, mirrors the CVS VTU/DOMParser     */
/* approach). The /result zip typically contains summary.xml (per-step */
/* aggregates) and statistics.xml / tripinfo.xml (trip aggregates).    */
/* ------------------------------------------------------------------ */

const round1 = (v) => Math.round(v * 10) / 10;

const numAttr = (el, attr) => {
  const v = parseFloat(el.getAttribute(attr));
  return Number.isFinite(v) ? v : null;
};

const parseXml = (text) => {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  return doc.getElementsByTagName("parsererror").length ? null : doc;
};

// summary.xml -> per-step time series
const parseSummary = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;

  const steps = Array.from(doc.getElementsByTagName("step"));
  if (!steps.length) return null;

  const time_s = [];
  const running_vehicles = [];
  const halting_vehicles = [];
  const mean_speed_m_s = [];
  const mean_waiting_time_s = [];

  steps.forEach((s) => {
    time_s.push(numAttr(s, "time"));
    running_vehicles.push(numAttr(s, "running"));
    halting_vehicles.push(numAttr(s, "halting"));

    // SUMO uses -1 as a "no data" sentinel for the mean fields.
    const ms = numAttr(s, "meanSpeed");
    mean_speed_m_s.push(ms != null && ms >= 0 ? ms : null);

    const mw = numAttr(s, "meanWaitingTime");
    mean_waiting_time_s.push(mw != null && mw >= 0 ? mw : null);
  });

  return {
    time_s,
    running_vehicles,
    halting_vehicles,
    mean_speed_m_s,
    mean_waiting_time_s,
  };
};

// statistics.xml -> scalar KPIs
const parseStatistics = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;

  const kpis = {};

  const vts = doc.getElementsByTagName("vehicleTripStatistics")[0];
  if (vts) {
    const count = numAttr(vts, "count");
    if (count != null) kpis.total_vehicles = count;

    const duration = numAttr(vts, "duration");
    if (duration != null) kpis.avg_trip_duration_s = round1(duration);

    const routeLength = numAttr(vts, "routeLength");
    if (routeLength != null) kpis.avg_route_length_m = round1(routeLength);

    const speed = numAttr(vts, "speed");
    if (speed != null) kpis.avg_speed_kmh = round1(speed * 3.6);

    const waiting = numAttr(vts, "waitingTime");
    if (waiting != null) kpis.avg_waiting_time_s = round1(waiting);

    const timeLoss = numAttr(vts, "timeLoss");
    if (timeLoss != null) kpis.avg_time_loss_s = round1(timeLoss);

    const departDelay = numAttr(vts, "departDelay");
    if (departDelay != null) kpis.avg_depart_delay_s = round1(departDelay);
  }

  const vehicles = doc.getElementsByTagName("vehicles")[0];
  if (vehicles) {
    const inserted = numAttr(vehicles, "inserted");
    if (inserted != null) kpis.vehicles_inserted = inserted;
  }

  const safety = doc.getElementsByTagName("safety")[0];
  if (safety) {
    const collisions = numAttr(safety, "collisions");
    if (collisions != null) kpis.collisions = collisions;
  }

  const teleports = doc.getElementsByTagName("teleports")[0];
  if (teleports) {
    const total = numAttr(teleports, "total");
    if (total != null) kpis.teleports = total;
  }

  return Object.keys(kpis).length ? kpis : null;
};

// Emissions totals from <emissions .../> children inside tripinfo entries.
// SUMO writes absolute emission values in mg (fuel in mg on SUMO >= 1.14,
// ml on older versions). Units confirmed as mg against the delivered package.
// Also derives per-VKT intensities (per vehicle-kilometre travelled). The
// mobility partner requires per-VKT rather than per-completed-trip, so that a
// scenario is not made to look cleaner simply because fewer trips completed.
const parseEmissionsFromTrips = (trips) => {
  const totals = { CO2_abs: 0, NOx_abs: 0, fuel_abs: 0, CO_abs: 0, PMx_abs: 0, HC_abs: 0 };
  let found = false;
  let routeLengthSum = 0; // metres, for VKT

  trips.forEach((t) => {
    const rl = numAttr(t, "routeLength");
    if (rl != null) routeLengthSum += rl;

    const em = t.getElementsByTagName("emissions")[0];
    if (!em) return;
    Object.keys(totals).forEach((attr) => {
      const v = numAttr(em, attr);
      if (v != null) {
        totals[attr] += v;
        found = true;
      }
    });
  });

  if (!found) return null;

  const vkt_km = routeLengthSum / 1000; // total vehicle-kilometres travelled
  const perVkt = (absMg, factor) =>
    vkt_km > 0 ? round1((absMg / vkt_km) * factor) : null;

  const kpis = {};

  if (totals.CO2_abs > 0) {
    kpis.total_co2_kg = round1(totals.CO2_abs / 1e6);
    // mg per km / 1000 = g per km
    const v = perVkt(totals.CO2_abs, 1 / 1e3);
    if (v != null) kpis.co2_per_vkt_g_km = v;
  }
  if (totals.NOx_abs > 0) {
    kpis.total_nox_g = round1(totals.NOx_abs / 1e3);
    const v = perVkt(totals.NOx_abs, 1); // mg per km
    if (v != null) kpis.nox_per_vkt_mg_km = v;
  }
  if (totals.fuel_abs > 0) {
    kpis.total_fuel_kg = round1(totals.fuel_abs / 1e6);
    const v = perVkt(totals.fuel_abs, 1 / 1e3); // g per km
    if (v != null) kpis.fuel_per_vkt_g_km = v;
  }
  if (totals.CO_abs > 0) kpis.total_co_g = round1(totals.CO_abs / 1e3);
  if (totals.PMx_abs > 0) kpis.total_pmx_g = round1(totals.PMx_abs / 1e3);
  if (totals.HC_abs > 0) kpis.total_hc_g = round1(totals.HC_abs / 1e3);
  if (vkt_km > 0) kpis.total_vkt_km = Math.round(vkt_km);

  return Object.keys(kpis).length ? kpis : null;
};

// tripinfo.xml -> aggregate scalar KPIs (fallback if no statistics.xml)
const parseTripinfo = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;

  const trips = Array.from(doc.getElementsByTagName("tripinfo"));
  if (!trips.length) return null;

  const sums = { duration: 0, routeLength: 0, waitingTime: 0, timeLoss: 0, speed: 0 };
  let speedCount = 0;

  trips.forEach((t) => {
    sums.duration += numAttr(t, "duration") || 0;
    sums.routeLength += numAttr(t, "routeLength") || 0;
    sums.waitingTime += numAttr(t, "waitingTime") || 0;
    sums.timeLoss += numAttr(t, "timeLoss") || 0;

    const dur = numAttr(t, "duration");
    const rl = numAttr(t, "routeLength");
    if (dur && rl) {
      sums.speed += rl / dur;
      speedCount += 1;
    }
  });

  const n = trips.length;
  const kpis = {
    total_vehicles: n,
    avg_trip_duration_s: round1(sums.duration / n),
    avg_route_length_m: round1(sums.routeLength / n),
    avg_waiting_time_s: round1(sums.waitingTime / n),
    avg_time_loss_s: round1(sums.timeLoss / n),
  };
  if (speedCount) kpis.avg_speed_kmh = round1((sums.speed / speedCount) * 3.6);

  const emissions = parseEmissionsFromTrips(trips);
  if (emissions) Object.assign(kpis, emissions);

  return kpis;
};

// Extract only the emissions totals from a tripinfo file, so they can be
// merged into KPIs sourced from statistics.xml.
const parseEmissionsOnly = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;
  const trips = Array.from(doc.getElementsByTagName("tripinfo"));
  if (!trips.length) return null;
  return parseEmissionsFromTrips(trips);
};

// Mean stop count per trip, from tripinfo `waitingCount` (the number of times
// a vehicle had to halt). Included to match the "Mean stop count per vehicle"
// metric in the handover comparison tables.
const parseStopCountOnly = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;

  const trips = Array.from(doc.getElementsByTagName("tripinfo"));
  if (!trips.length) return null;

  let total = 0;
  let found = false;
  trips.forEach((t) => {
    const v = numAttr(t, "waitingCount");
    if (v != null) {
      total += v;
      found = true;
    }
  });

  if (!found) return null;
  return { avg_stop_count: Math.round((total / trips.length) * 100) / 100 };
};

/* ------------------------------------------------------------------ */
/* Time-of-day handling                                                */
/*                                                                     */
/* Per the mobility partner: simulation second 0 corresponds to 00:00  */
/* clock time and the run spans 0-86400s (24:00). Simulation seconds    */
/* are formatted DIRECTLY as HH:MM with no UTC/local-time conversion.   */
/* Only the daytime window 07:00-20:00 is presented, i.e. intervals     */
/* with 25200 <= begin < 72000. Series are aggregated to whole clock    */
/* hours (07:00 ... 19:00) and the view draws them as stepped lines     */
/* (no smoothing), each step covering the hour it labels.               */
/* ------------------------------------------------------------------ */

const DAY_START_S = 25200; // 07:00
const DAY_END_S = 72000; // 20:00 (exclusive)
const HOUR_S = 3600;

const DAYTIME_WINDOW_LABEL = "Daytime only · 07:00–20:00 (simulation clock)";
const TIME_AXIS_LABEL = "Time of day";

// Format simulation seconds directly as HH:MM (no timezone conversion).
const secondsToClock = (sec) => {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

const inDaytime = (beginS) =>
  beginS != null && beginS >= DAY_START_S && beginS < DAY_END_S;

const hourBinStart = (beginS) => Math.floor(beginS / HOUR_S) * HOUR_S;

// The fixed hourly clock axis for the daytime window: 07:00 ... 19:00.
const buildDaytimeHours = () => {
  const starts = [];
  for (let t = DAY_START_S; t < DAY_END_S; t += HOUR_S) starts.push(t);
  return starts;
};

// Motorized school-frontage detectors (both directions). Bicycles and the
// bike-lane detectors are deliberately excluded so the headline volume series
// counts motor vehicles only, matching the validated "frontage motorized
// passages" KPI (verified: full-day sum of nVehContrib = 8610 on baseline).
const MOTORIZED_DETECTORS = new Set([
  "school_car_dir_pos",
  "school_car_dir_neg",
  "school_large_dir_pos",
  "school_large_dir_neg",
  "school_motorcycle_dir_pos",
  "school_motorcycle_dir_neg",
]);

// E1 induction-loop detector -> hourly daytime series for the school frontage:
//   - motorized passages per hour (sum of nVehContrib over the motorized
//     detectors, both directions) -> headline traffic-volume series,
//   - frontage mean speed (km/h, nVehContrib-weighted),
//   - frontage occupancy (%).
// Returns { hours, passages, speedKmh, occupancy } on the daytime hour grid,
// or null if the detector carries no usable daytime data.
const parseE1Hourly = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;

  const intervals = Array.from(doc.getElementsByTagName("interval"));
  if (!intervals.length) return null;

  const hours = buildDaytimeHours();
  const idx = new Map(hours.map((h, i) => [h, i]));
  const passages = hours.map(() => 0);
  const speedW = hours.map(() => 0);
  const speedWN = hours.map(() => 0);
  const occSum = hours.map(() => 0);
  const occN = hours.map(() => 0);
  let sawMotorized = false;

  intervals.forEach((iv) => {
    const begin = numAttr(iv, "begin");
    if (!inDaytime(begin)) return;
    if (!MOTORIZED_DETECTORS.has(iv.getAttribute("id"))) return;

    const i = idx.get(hourBinStart(begin));
    if (i == null) return;
    sawMotorized = true;

    const n = numAttr(iv, "nVehContrib") || 0;
    passages[i] += n;

    const sp = numAttr(iv, "speed");
    if (sp != null && sp >= 0 && n > 0) {
      speedW[i] += sp * n; // m/s weighted by passages
      speedWN[i] += n;
    }

    const occ = numAttr(iv, "occupancy");
    if (occ != null) {
      occSum[i] += occ;
      occN[i] += 1;
    }
  });

  if (!sawMotorized) return null;

  return {
    hours,
    passages: passages.map((v) => Math.round(v)),
    // m/s -> km/h
    speedKmh: speedW.map((w, i) => (speedWN[i] > 0 ? round1((w / speedWN[i]) * 3.6) : null)),
    occupancy: occSum.map((v, i) => (occN[i] > 0 ? round1(v / occN[i]) : null)),
  };
};

// edge mean-data -> hourly daytime "edge-exit events per hour" (sum of SUMO
// `left` across edges). This is a network-wide throughput DIAGNOSTIC, kept
// alongside the detector volume as a secondary series, not the headline.
const parseEdgeExitHourly = (xmlText) => {
  const doc = parseXml(xmlText);
  if (!doc) return null;

  const intervals = Array.from(doc.getElementsByTagName("interval"));
  if (!intervals.length) return null;

  const hours = buildDaytimeHours();
  const idx = new Map(hours.map((h, i) => [h, i]));
  const left = hours.map(() => 0);
  let saw = false;

  intervals.forEach((iv) => {
    const begin = numAttr(iv, "begin");
    if (!inDaytime(begin)) return;
    const i = idx.get(hourBinStart(begin));
    if (i == null) return;

    let leftSum = 0;
    let found = false;
    Array.from(iv.getElementsByTagName("edge")).forEach((e) => {
      const v = numAttr(e, "left");
      if (v != null) {
        leftSum += v;
        found = true;
      }
    });
    if (found) {
      left[i] += leftSum;
      saw = true;
    }
  });

  if (!saw) return null;
  return { hours, edgeExits: left.map((v) => Math.round(v)) };
};

// Assemble the daytime hourly time-series payload consumed by the view.
// Shape (mode "clock-hourly"):
//   { mode, window, xTitle, xLabels:[HH:MM], series:[{key,label,data,role,unit,direction}] }
// `role`: "primary" (frontage measurement) | "diagnostic" (network-wide proxy).
const buildClockHourlySeries = (e1, edge) => {
  if (!e1 && !edge) return null;
  const hours = (e1 || edge).hours;
  const xLabels = hours.map(secondsToClock);
  const series = [];

  if (e1) {
    series.push({
      key: "motorized_passages_per_h",
      label: "School-frontage motorized passages per hour",
      data: e1.passages,
      role: "primary",
      unit: "veh/h",
      direction: "neutral",
    });
    if (e1.speedKmh.some((v) => v != null)) {
      series.push({
        key: "frontage_mean_speed_kmh",
        label: "Frontage mean speed",
        data: e1.speedKmh,
        role: "primary",
        unit: "km/h",
        direction: "lower",
      });
    }
    if (e1.occupancy.some((v) => v != null)) {
      series.push({
        key: "frontage_occupancy_pct",
        label: "Frontage detector occupancy",
        data: e1.occupancy,
        role: "primary",
        unit: "%",
        direction: "neutral",
      });
    }
  }

  if (edge) {
    series.push({
      key: "edge_exit_events_per_h",
      label: "Edge-exit events per hour (network diagnostic)",
      data: edge.edgeExits,
      role: "diagnostic",
      unit: "events/h",
      direction: "neutral",
    });
  }

  if (!series.length) return null;

  return {
    mode: "clock-hourly",
    window: DAYTIME_WINDOW_LABEL,
    xTitle: TIME_AXIS_LABEL,
    xLabels,
    series,
  };
};

// SUMO writes the engine version into the header comment of every output
// file, e.g. "<!-- generated on 2026-10-21T10:14:01 by Eclipse SUMO sumo 1.28.0".
// This is the version that actually ran, as opposed to the validated-package
// reference in release.js. Only the first part of the file is scanned.
const SUMO_HEADER_VERSION_RE = /Eclipse SUMO\s+\w+\s+v?(\d+\.\d+\.\d+)/i;

export const parseSumoVersionFromHeader = (text) => {
  if (!text) return null;
  const m = String(text).slice(0, 2000).match(SUMO_HEADER_VERSION_RE);
  return m ? m[1] : null;
};

// True when a time-series payload actually carries drawable data, for either
// the structured "clock-hourly" shape or the legacy per-field arrays shape.
const hasSeries = (ts) => {
  if (!ts) return false;
  if (ts.mode === "clock-hourly") {
    return Array.isArray(ts.series) && ts.series.some((s) => Array.isArray(s.data) && s.data.length >= 2);
  }
  return Object.values(ts).some((v) => Array.isArray(v) && v.length >= 2);
};

// Unzip the result package and turn it into { kpis, timeseries, _files }.
// SUMO output files here are XML but use a non-.xml extension (e.g.
// "stats.schoolcal.all"), so we match by leading keyword, not extension.
const parseResultZip = async (blob) => {
  const zip = await JSZip.loadAsync(blob);

  const files = Object.keys(zip.files).filter((name) => !zip.files[name].dir);

  const basename = (name) => name.split("/").pop();
  const find = (re) => files.find((name) => re.test(basename(name)));
  // Each file is decompressed once and reused (tripinfo, the largest file, is
  // otherwise read for emissions, stop counts and possibly the version header).
  const fileCache = new Map();
  const readFile = (name) => {
    if (!fileCache.has(name)) fileCache.set(name, zip.files[name].async("string"));
    return fileCache.get(name);
  };

  const summaryName = find(/summary/i);
  const statsName = find(/stat/i); // stats / statistics
  const tripName = find(/tripinfo/i); // tripinfo / tripinfos
  const e1Name = find(/(^|[^a-z])e1|induction|detector/i);
  const edgeName = find(/edge.?data|meandata|edgedata/i);
  const emissionName = find(/emission/i);

  // sumoRuntimeVersion: engine version read from the output headers (string),
  // kept outside `kpis` so it is never rendered as a numeric KPI card.
  const result = { kpis: {}, timeseries: {}, sumoRuntimeVersion: null, _files: files };

  // --- actual SUMO runtime version, from the first output header found ---
  // Small files first (stats / detector) so the large tripinfo is only read
  // if nothing else carries a header.
  const headerCandidates = [statsName, e1Name, summaryName, edgeName, tripName, ...files]
    .filter(Boolean)
    .filter((name, i, arr) => arr.indexOf(name) === i);
  for (const name of headerCandidates) {
    const version = parseSumoVersionFromHeader(await readFile(name));
    if (version) {
      result.sumoRuntimeVersion = version;
      break;
    }
  }

  // --- scalar KPIs: prefer statistics, fall back to aggregated tripinfo ---
  if (statsName) {
    const kpis = parseStatistics(await readFile(statsName));
    if (kpis) result.kpis = kpis;
  }
  if (!Object.keys(result.kpis).length && tripName) {
    const kpis = parseTripinfo(await readFile(tripName));
    if (kpis) result.kpis = kpis;
  }

  // --- emissions (CO2 / NOx / fuel): merge in if present anywhere ---
  const hasEmissions = Object.keys(result.kpis).some((k) =>
    /co2|nox|fuel|pmx|_co_/i.test(k)
  );
  if (!hasEmissions) {
    if (emissionName) {
      const em = parseEmissionsOnly(await readFile(emissionName));
      if (em) Object.assign(result.kpis, em);
    } else if (tripName) {
      const em = parseEmissionsOnly(await readFile(tripName));
      if (em) Object.assign(result.kpis, em);
    }
  }

  // --- mean stop count per trip: only available from tripinfo ---
  if (!("avg_stop_count" in result.kpis) && tripName) {
    const sc = parseStopCountOnly(await readFile(tripName));
    if (sc) Object.assign(result.kpis, sc);
  }

  // --- time series --------------------------------------------------
  // Primary: the school-frontage detector, aggregated to whole clock hours
  // over the daytime window (07:00-20:00). The headline is motorized
  // passages per hour; the network-wide edge-exit count (if the edge mean-
  // data file is present) is attached as a labelled secondary diagnostic.
  // Legacy per-step summary output is used only if no detector is available.
  const e1Hourly = e1Name ? parseE1Hourly(await readFile(e1Name)) : null;
  const edgeHourly = edgeName ? parseEdgeExitHourly(await readFile(edgeName)) : null;
  const clockHourly = buildClockHourlySeries(e1Hourly, edgeHourly);

  if (hasSeries(clockHourly)) {
    result.timeseries = clockHourly;
  } else if (summaryName) {
    const series = parseSummary(await readFile(summaryName));
    if (hasSeries(series)) result.timeseries = series;
  }

  return result;
};

/* ------------------------------------------------------------------ */
/* Thunks                                                              */
/* ------------------------------------------------------------------ */

export const executeSumoSimulation = createAsyncThunk(
  "sumoSimulation/execute",
  async (scenario, { rejectWithValue }) => {
    try {
      if (!AUTH_TOKEN) {
        throw new Error("SUMO auth token is missing from environment variables.");
      }

      const response = await fetch(`${API_BASE_URL}/execute`, {
        method: "POST",
        headers: getHeaders(true),
        body: JSON.stringify({ scenario }),
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(
          response,
          "Failed to execute SUMO simulation."
        );
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const checkSumoStatus = createAsyncThunk(
  "sumoSimulation/status",
  async (uuid, { rejectWithValue }) => {
    try {
      if (!AUTH_TOKEN) {
        throw new Error("SUMO auth token is missing from environment variables.");
      }

      const response = await fetch(`${API_BASE_URL}/status/${uuid}`, {
        method: "GET",
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const text = await getErrorMessage(
          response,
          "Failed to check SUMO simulation status."
        );

        // Right after /execute, the run may not be registered yet and the
        // endpoint returns 404 "not found". That's a transient race, not a
        // real failure, so report it as pending and let polling continue.
        if (response.status === 404 || /not\s*found/i.test(text)) {
          return { status: "pending", _transient: true };
        }

        return rejectWithValue(text);
      }

      const data = await response.json();

      const normalized = String(data?.status || data?.state || "").toLowerCase();
      if (normalized === "not found" || normalized === "not_found") {
        return { status: "pending", _transient: true };
      }

      return data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSumoKpis = createAsyncThunk(
  "sumoSimulation/kpis",
  async (uuid, { rejectWithValue }) => {
    try {
      if (!AUTH_TOKEN) {
        throw new Error("SUMO auth token is missing from environment variables.");
      }

      const response = await fetch(`${API_BASE_URL}/result/${uuid}`, {
        method: "GET",
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(
          response,
          "Failed to retrieve SUMO KPIs."
        );
        throw new Error(errorMessage);
      }

      const contentType = (response.headers.get("content-type") || "").toLowerCase();

      // If the backend ever exposes JSON KPIs directly, use them as-is.
      if (contentType.includes("application/json")) {
        return await response.json();
      }

      // Otherwise the result is a file package (zip), parsed client-side.
      const blob = await response.blob();
      return await parseResultZip(blob);
    } catch (error) {
      return rejectWithValue(
        error?.message || "Could not read the SUMO result package."
      );
    }
  }
);

export const downloadSumoResult = createAsyncThunk(
  "sumoSimulation/result",
  async (uuid, { rejectWithValue }) => {
    try {
      if (!AUTH_TOKEN) {
        throw new Error("SUMO auth token is missing from environment variables.");
      }

      const response = await fetch(`${API_BASE_URL}/result/${uuid}`, {
        method: "GET",
        headers: getHeaders(false),
      });

      if (!response.ok) {
        const errorMessage = await getErrorMessage(
          response,
          "Failed to retrieve SUMO simulation results."
        );
        throw new Error(errorMessage);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `sumo-result-${uuid}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      return true;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Reads the release identifiers from the /execute (or /status) response.
// The backend returns repo_version + model_version with each execution
// (release 1.1.0 onward); these must travel with the run so results from
// different model generations are never mixed or compared.
// The API does not currently return the SUMO engine version; if the partner
// adds a `sumo_version` field later it is picked up here. Until then the
// runtime version comes from the output XML headers (see parseResultZip).
const readVersions = (payload) => ({
  repoVersion: payload?.repo_version || payload?.repository_version || null,
  modelVersion: payload?.model_version || null,
  sumoVersion: payload?.sumo_version || null,
});

// Two runs are comparable only if they come from the same model release.
export const sameModelRelease = (a, b) =>
  Boolean(a && b && a.repoVersion === b.repoVersion && a.modelVersion === b.modelVersion);

const initialState = {
  scenario: "scenarioA",
  uuid: null,
  status: null,
  statusDetails: null,
  kpis: null,
  // repo_version / model_version of the CURRENT run, from the execute response.
  runVersion: { repoVersion: null, modelVersion: null },
  // Cached KPIs from the most recent successful "baseline" run, tagged with the
  // model release they came from. Kept so other scenarios can be compared
  // against the reference case without re-running it, but only within the same
  // release: baseline from an older model must not be compared against a newer
  // scenario run.
  baselineKpis: null,
  baselineRunAt: null,
  baselineVersion: { repoVersion: null, modelVersion: null },
  runStartedAt: null,
  loading: false,
  statusLoading: false,
  downloadLoading: false,
  kpisLoading: false,
  error: null,
  statusError: null,
  kpiError: null,
};

const sumoSimulationSlice = createSlice({
  name: "sumoSimulation",
  initialState,
  reducers: {
    setScenario: (state, action) => {
      state.scenario = action.payload;
    },

    clearBaselineKpis: (state) => {
      state.baselineKpis = null;
      state.baselineRunAt = null;
      state.baselineVersion = { repoVersion: null, modelVersion: null };
    },

    // Reset clears the current run but deliberately keeps the cached baseline,
    // so the user doesn't have to re-run baseline to keep comparing scenarios.
    resetSumoSimulation: (state) => ({
      ...initialState,
      scenario: state.scenario,
      baselineKpis: state.baselineKpis,
      baselineRunAt: state.baselineRunAt,
      baselineVersion: state.baselineVersion,
    }),
  },
  extraReducers: (builder) => {
    builder
      .addCase(executeSumoSimulation.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.statusError = null;
        state.kpiError = null;
        state.uuid = null;
        state.status = null;
        state.statusDetails = null;
        state.kpis = null;
        state.runVersion = { repoVersion: null, modelVersion: null };
        state.runStartedAt = Date.now();
      })

      .addCase(executeSumoSimulation.fulfilled, (state, action) => {
        state.loading = false;

        state.uuid =
          action.payload?.run_uuid ||
          action.payload?.uuid ||
          action.payload?.id ||
          action.payload?.simulation_uuid ||
          null;

        state.status = action.payload?.status || "submitted";
        state.statusDetails = action.payload || null;
        state.runVersion = readVersions(action.payload);
      })

      .addCase(executeSumoSimulation.rejected, (state, action) => {
        state.loading = false;
        state.runStartedAt = null;
        state.error = action.payload || "Failed to execute SUMO simulation.";
      })

      .addCase(checkSumoStatus.pending, (state) => {
        state.statusLoading = true;
        state.statusError = null;
      })

      .addCase(checkSumoStatus.fulfilled, (state, action) => {
        state.statusLoading = false;
        state.statusError = null;

        state.status =
          action.payload?.status ||
          action.payload?.state ||
          "unknown";

        // Keep last informative details; don't overwrite with the transient marker.
        if (!action.payload?._transient) {
          state.statusDetails = action.payload || null;
        }
      })

      .addCase(checkSumoStatus.rejected, (state, action) => {
        state.statusLoading = false;
        // Non-blocking: a single failed poll shouldn't flash a red error.
        state.statusError =
          action.payload || "Could not refresh the simulation status.";
      })

      .addCase(fetchSumoKpis.pending, (state) => {
        state.kpisLoading = true;
        state.kpiError = null;
      })

      .addCase(fetchSumoKpis.fulfilled, (state, action) => {
        state.kpisLoading = false;
        state.kpis = action.payload || null;

        // If these results came from the baseline run, cache them as the
        // reference case for scenario comparison.
        const ranScenario = String(
          state.statusDetails?.scenario || state.scenario || ""
        ).toLowerCase();

        if (ranScenario === "baseline" && action.payload) {
          state.baselineKpis = action.payload;
          state.baselineRunAt = Date.now();
          state.baselineVersion = { ...state.runVersion };
        }
      })

      .addCase(fetchSumoKpis.rejected, (state, action) => {
        state.kpisLoading = false;
        state.kpis = null;
        state.kpiError = action.payload || "Failed to retrieve SUMO KPIs.";
      })

      .addCase(downloadSumoResult.pending, (state) => {
        state.downloadLoading = true;
        state.error = null;
      })

      .addCase(downloadSumoResult.fulfilled, (state) => {
        state.downloadLoading = false;
      })

      .addCase(downloadSumoResult.rejected, (state, action) => {
        state.downloadLoading = false;
        state.error =
          action.payload || "Failed to retrieve SUMO simulation results.";
      });
  },
});

export const { setScenario, clearBaselineKpis, resetSumoSimulation } =
  sumoSimulationSlice.actions;

export default sumoSimulationSlice.reducer;